import React from "react";
import { Text } from "@/components/text/Text";

import { Instagram, WhatsApp } from "@mui/icons-material";

import { contactInfo } from "@/Constants";

import hair1 from "@/assets/img/hair/hair1.jpeg";
import hair2 from "@/assets/img/hair/hair2.jpeg";
import hair3 from "@/assets/img/hair/hair3.jpeg";
import hair4 from "@/assets/img/hair/hair4.jpeg";
import hair5 from "@/assets/img/hair/hair5.jpg";
import hair6 from "@/assets/img/hair/hair6.jpg";

import stars from "@/assets/icons/stars.svg";
import DefaultButton from "@/components/buttons";
import PhotoStairs from "@/components/misc/PhotoStairs";
import { useMediaQuery, useTheme } from "@mui/material";
import { useLanguageContext } from "@/context/LanguageContext";
import { useStyle } from "@/context/StyleContext";

const hairImages = [hair1, hair2, hair3, hair4, hair6];

const Header: React.FC = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [videoError, setVideoError] = React.useState(false);

  const { lang } = useLanguageContext();

  const headerProps = {
    titleLine1: lang.t("landing.header.titleLine1"),
    titleLine2: lang.t("landing.header.titleLine2"),
    subtitle: lang.t("landing.header.subtitle"),
    bookButtonText: lang.t("landing.header.buttons.book"),
    readMoreButtonText: lang.t("landing.header.buttons.moreInfo"),
  };

  return (
    <header
      style={{
        position: "relative",
        width: "100%",
        paddingTop: "20px",
        paddingBottom: "20px",
      }}
    >
      {videoError || isMobile ? (
        <HeaderImage />
      ) : (
        <HeaderVideo onError={() => setVideoError(true)} />
      )}
      <HeaderTextContainer isMobile={isMobile} {...headerProps} />
      <SocialIconsMenu />
    </header>
  );
};

export default Header;

type HeaderTextContainerProps = {
  titleLine1?: string;
  titleLine2?: string;
  subtitle?: string;
  bookButtonText?: string;
  readMoreButtonText?: string;
  isMobile?: boolean;
};

const HeaderTextContainer = (props: HeaderTextContainerProps) => {
  const muiTheme = useTheme();
  const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "md"));
  const { isMobile } = props;

  return (
    <div
      style={{
        height: "100%",
        position: "absolute",
        top: 0,
        paddingLeft: isMobile ? "10px" : isTablet ? "210px" : "420px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <img src={stars} alt="Stars" />
        </div>

        <Text type={isMobile ? "h2" : "h1"} style={{ margin: 0 }}>
          {props.titleLine1} <br /> {props.titleLine2}
        </Text>
        <Text>{props.subtitle}</Text>
        <div style={{ display: "flex", columnGap: "24px" }}>
          <DefaultButton
            style={{ marginTop: "50px" }}
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({
                top:
                  document.getElementById("section-book-now")?.offsetTop || 0,
                behavior: "smooth",
              });
            }}
          >
            {props.bookButtonText}
          </DefaultButton>
          <DefaultButton
            style={{ marginTop: "50px" }}
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({
                top:
                  document.getElementById("section-our-style")?.offsetTop || 0,
                behavior: "smooth",
              });
            }}
          >
            {props.readMoreButtonText}
          </DefaultButton>
        </div>
      </div>
    </div>
  );
};

const SocialIconsMenu = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up("lg"));

  const icons = [
    {
      icon: <WhatsApp style={{ fontSize: "48px", color: "#25D366" }} />,
      href: isMobile
        ? `whatsapp://send?phone=${contactInfo.whatsAppNumber}`
        : `https://web.whatsapp.com/send?phone=${contactInfo.whatsAppNumber}`,
      key: "whatsapp",
    },
    {
      icon: <Instagram style={{ fontSize: "48px", color: "#C13584" }} />,
      href: isMobile
        ? `instagram://user?username=${contactInfo.instagram}`
        : `https://instagram.com/${contactInfo.instagram}`,
      key: "instagram",
    },
  ];

  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: isTablet ? "flex-start" : "center",
        pointerEvents: "none", // Prevents interaction with the social icons
      }}
    >
      {!isMobile && isLargeScreen && (
        <div
          style={{
            pointerEvents: "auto",
          }}
        >
          <PhotoStairs
            photos={hairImages.sort(() => Math.random() - 0.5).slice(0, 3)}
            stepOffset={90}
          />
        </div>
      )}
      <div
        style={{
          display: "flex",
          height: "40%",
          flexDirection: isTablet ? "row" : "column",
          columnGap: isTablet ? "24px" : undefined,
          padding: "48px",
          justifyContent: "space-evenly",
          rowGap: "24px",
        }}
      >
        {icons.map((icon, index) => (
          <Text
            key={`social-icon-${icon.key}-${index}`}
            type="a"
            target="_blank"
            href={icon.href}
            style={{ pointerEvents: "auto" }}
          >
            {icon.icon}
          </Text>
        ))}
      </div>
    </div>
  );
};

const HeaderImage = () => {
  const { colors, theme } = useStyle();

  return (
    <div
      style={{
        position: "static",
        top: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          maxWidth: "480px",
          height: "640px",
          backgroundColor: "#f0f0f0",
          borderTopRightRadius: "250px",
          borderColor: colors[theme].border,
          position: "relative",
          borderStyle: "solid",
          borderWidth: "1px",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            borderTopRightRadius: "250px",
            backgroundColor: colors[theme].background,
            opacity: 0.55, // OVERLAY opacity
            transition: "opacity 0.3s ease-in-out",
          }}
        ></div>
        <img
          src={hair5}
          alt="Hair Style"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderTopRightRadius: "250px",
          }}
        />
      </div>
    </div>
  );
};

const HeaderVideo = ({ onError }: { onError?: () => void }) => {
  const { colors, theme } = useStyle();

  return (
    <div
      style={{
        position: "static",
        top: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          maxWidth: "480px",
          height: "640px",
          backgroundColor: "#f0f0f0",
          borderTopRightRadius: "250px",
          borderColor: colors[theme].border,
          position: "relative",
          borderStyle: "solid",
          borderWidth: "1px",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            borderTopRightRadius: "250px",
            backgroundColor: colors[theme].background,
            opacity: 0.55, // OVERLAY opacity
            transition: "opacity 0.3s ease-in-out",
          }}
        ></div>
        <video
          muted
          autoPlay
          loop
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderTopRightRadius: "250px",
          }}
          onError={onError}
        >
          <source
            src={
              "https://hhrwwwphotos.blob.core.windows.net/wwwphotos/hhr_cropped_small.mp4"
            }
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
};
